import { Scene, PerspectiveCamera, WebGLRenderer, AxesHelper, Color, Fog, HemisphereLight, HemisphereLightHelper, DirectionalLight, DirectionalLightHelper, PlaneBufferGeometry, MeshPhongMaterial, Mesh, SphereBufferGeometry, ShaderMaterial, BackSide, BoxGeometry, TextureLoader, RepeatWrapping, MeshBasicMaterial, LoadingManager, Matrix4, sRGBEncoding, GammaEncoding } from 'three';
import { WEBGL } from 'three/examples/jsm/WebGL';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { CinematicCamera } from 'three/examples/jsm/cameras/CinematicCamera';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { CSG } from 'three-csg-ts';
import loadingScreenCss from './loading-screen-css';
import { CutoutDirection } from './drawing';
function round(value, decimalPlaces) {
    return parseFloat(value.toFixed(decimalPlaces));
}
export class Renderer {
    constructor(options) {
        this.options = options;
        this.scaleFactor = 10000;
        this.deckingMeshes = new Array();
        this.columnMeshes = new Array();
        this.beamMeshes = new Array();
        this.cpMeshes = new Array();
        this.bpMeshes = new Array();
        this.tagMeshes = new Array();
        this.angleMeshes = new Array();
        this.joistMeshes = new Array();
        this.cleatMeshes = new Array();
        this.manager = new LoadingManager();
        this.demo = true;
        this.deckingOffset = 0.27;
        this.standardBeamOffset = 0.155;
        this.handleFirstTouchClick = (e) => {
            e.stopPropagation();
            e.preventDefault();
            this.demo = false;
            this.options.containerElement.removeEventListener('click', this.handleFirstTouchClick);
            this.options.containerElement.removeEventListener('touchstart', this.handleFirstTouchClick);
        };
        if (this.options.baseUrl === undefined) {
            this.options.baseUrl = 'https://raw.githubusercontent.com/Hi-Level/3d-renderer-models/master/';
        }
        if (WEBGL.isWebGLAvailable()) {
            this.canRender = true;
            this.updateWidthHeight();
            this.createLoadingScreen();
            this.manager.onLoad = () => {
                this.loadingScreen.classList.add('fade-out');
                this.loadingScreen.addEventListener('transitionend', this.onTransitionEnd);
            };
            this.setupScene();
            this.animate();
        }
        else {
            this.canRender = false;
            const warning = WEBGL.getWebGLErrorMessage();
            this.options.containerElement.appendChild(warning);
        }
    }
    get currentCamera() {
        return this.demo ? this.cinematicCamera : this.camera;
    }
    updateWidthHeight() {
        this.width = this.options.containerElement.clientWidth;
        this.height = this.options.containerElement.clientHeight;
    }
    setupScene() {
        this.options.containerElement.addEventListener('click', this.handleFirstTouchClick);
        this.options.containerElement.addEventListener('touchstart', this.handleFirstTouchClick);
        this.scene = new Scene();
        this.camera = new PerspectiveCamera(30, this.width / this.height, 1, 5000);
        this.cinematicCamera = new CinematicCamera(30, this.width / this.height, 1, 5000);
        this.controls = new OrbitControls(this.camera, this.options.containerElement);
        this.controls.minDistance = 5;
        this.controls.maxDistance = 500;
        this.renderer = new WebGLRenderer({ antialias: true });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(this.width, this.height);
        this.renderer.outputEncoding = sRGBEncoding;
        this.options.containerElement.appendChild(this.renderer.domElement);
        if (this.options.enableHelpers) {
            const axesHelper = new AxesHelper(5);
            this.scene.add(axesHelper);
        }
        this.camera.position.set(0, 250, 250);
        this.cinematicCamera.position.set(0, 200, 250);
        this.controls.update();
        this.scene.background = new Color().setHSL(0.6, 0, 1);
        this.scene.fog = new Fog(this.scene.background.getHex(), 1, 5000);
        this.hemiLight = new HemisphereLight(0xffffff, 0xffffff, 0.6);
        this.hemiLight.color.setHSL(0.6, 1, 0.6);
        this.hemiLight.groundColor.setHSL(0.095, 1, 0.75);
        this.hemiLight.position.set(0, 50, 0);
        this.scene.add(this.hemiLight);
        if (this.options.enableHelpers) {
            const hemiLightHelper = new HemisphereLightHelper(this.hemiLight, 10);
            this.scene.add(hemiLightHelper);
        }
        this.dirLight = new DirectionalLight(0xffffff, 1);
        this.dirLight.color.setHSL(0.1, 1, 0.95);
        this.dirLight.position.set(-1, 1.75, 1);
        this.dirLight.position.multiplyScalar(30);
        this.scene.add(this.dirLight);
        if (this.options.enableHelpers) {
            const dirLightHelper = new DirectionalLightHelper(this.dirLight, 10);
            this.scene.add(dirLightHelper);
        }
        const groundGeo = new PlaneBufferGeometry(10000, 10000);
        const groundMat = new MeshPhongMaterial({
            color: 0xffffff,
            specular: 0x050505
        });
        groundMat.color.setHSL(0.095, 1, 0.75);
        const ground = new Mesh(groundGeo, groundMat);
        ground.rotation.x = -Math.PI / 2;
        ground.position.y = -33;
        this.scene.add(ground);
        const vertexShader = `
      varying vec3 vWorldPosition;
      void main() {
          vec4 worldPosition = modelMatrix * vec4( position, 1.0 );
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
      }
    `;
        const fragmentShader = `
      uniform vec3 topColor;
      uniform vec3 bottomColor;
      uniform float offset;
      uniform float exponent;
      varying vec3 vWorldPosition;
      void main() {
          float h = normalize( vWorldPosition + offset ).y;
          gl_FragColor = vec4( mix( bottomColor, topColor, max( pow( max( h , 0.0), exponent ), 0.0 ) ), 1.0 );
      }
    `;
        const uniforms = {
            topColor: { value: new Color(0x0077ff) },
            bottomColor: { value: new Color(0xffffff) },
            offset: { value: 33 },
            exponent: { value: 0.6 }
        };
        uniforms.topColor.value.copy(this.hemiLight.color);
        this.scene.fog.color.copy(uniforms.bottomColor.value);
        const skyGeo = new SphereBufferGeometry(4000, 32, 15);
        const skyMat = new ShaderMaterial({
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            uniforms: uniforms,
            side: BackSide
        });
        const sky = new Mesh(skyGeo, skyMat);
        this.scene.add(sky);
        window.addEventListener('resize', this.onWindowResize.bind(this), false);
    }
    animate() {
        requestAnimationFrame(this.animate.bind(this));
        this.controls.update();
        if (this.deckingMeshes && this.deckingMeshes.length > 0 && this.demo) {
            const position = this.deckingMeshes.length === 1 ?
                this.deckingMeshes[0].position :
                this.deckingMeshes[this.deckingMeshes.length - 1].position;
            const speed = Date.now() * 0.00025;
            this.cinematicCamera.position.x = Math.cos(speed) * 400;
            this.cinematicCamera.position.z = Math.sin(speed) * 400;
            this.cinematicCamera.lookAt(position);
            this.cinematicCamera.updateMatrixWorld();
        }
        this.renderer.render(this.scene, this.currentCamera);
    }
    onWindowResize() {
        this.updateWidthHeight();
        this.cinematicCamera.aspect = this.width / this.height;
        this.camera.aspect = this.width / this.height;
        this.cinematicCamera.updateProjectionMatrix();
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.width, this.height);
    }
    destroy() {
        this.clearStage();
        this.renderer.dispose();
    }
    clearStage() {
        if (!this.canRender) {
            return;
        }
        if (this.options.enableHelpers) {
            console.log('clear start:', this.renderer.info.memory);
        }
        this.clearMeshes(this.deckingMeshes);
        this.clearMeshes(this.columnMeshes);
        this.clearMeshes(this.beamMeshes);
        this.clearMeshes(this.cpMeshes);
        this.clearMeshes(this.bpMeshes);
        this.clearMeshes(this.tagMeshes);
        this.clearMeshes(this.angleMeshes);
        this.clearMeshes(this.joistMeshes);
        this.clearMeshes(this.cleatMeshes);
        if (this.options.enableHelpers) {
            console.log('clear end:  ', this.renderer.info.memory);
        }
    }
    clearMeshes(meshArr) {
        for (const mesh of meshArr) {
            this.removeObject(mesh);
        }
        meshArr = [];
    }
    removeObject(obj) {
        if (obj.children) {
            for (const child of obj.children) {
                this.removeObject(child);
            }
        }
        if (obj instanceof Mesh) {
            obj.geometry.dispose();
            if (obj.material instanceof Array) {
                obj.material.map(m => m.dispose());
            }
            else {
                if (obj.material instanceof MeshBasicMaterial) {
                    obj.material.map.dispose();
                }
                obj.material.dispose();
            }
        }
        this.scene.remove(obj);
    }
    getImageData() {
        if (!this.canRender) {
            return;
        }
        this.renderer.render(this.scene, this.currentCamera);
        return this.renderer.domElement.toDataURL();
    }
    centerView() {
        if (!this.canRender) {
            return;
        }
        this.demo = false;
        this.camera.position.set(0, 250, 250);
    }
    loadFloor(drawing) {
        if (!this.canRender) {
            return;
        }
        this.clearStage();
        const midX = (drawing.Floor.Size.XDim * 10) / 2;
        const midY = (drawing.Floor.Size.TotalZDim * 10) / 2;
        const midZ = (drawing.Floor.Size.YDim * 10) / 2;
        this.controls.target.x = midX;
        this.controls.target.y = midY;
        this.controls.target.z = midZ;
        this.hemiLight.position.set(midX, midY + 100, midZ);
        this.dirLight.position.y = midY + 100;
        this.dirLight.position.z = drawing.Floor.Size.YDim * 10;
        let currentHeight = 0;
        for (const tierData of drawing.Floor.Tiers) {
            if (tierData.Number === 1) {
                this.createBaseplates(tierData.Columns);
            }
            this.createColumns(tierData.Columns, tierData.Beams, currentHeight, tierData.Size.ZDim, drawing.Floor.Tiers.length > 1, drawing.Floor.Tiers.length === tierData.Number);
            currentHeight += tierData.Size.ZDim;
            this.createBeams(tierData.Beams, currentHeight, drawing.Floor.BayDirection);
            this.createJoists(tierData.Joists, tierData.Columns, currentHeight, drawing.Floor.BayDirection);
            this.createCleats(tierData.Joists, tierData.Beams, tierData.Columns, currentHeight, drawing.Floor.BayDirection);
            this.createAngles(tierData.Angles, currentHeight);
            if (tierData.Columns[0].IsTagged) {
                this.createBeamTags(tierData.Beams, currentHeight, drawing.Floor.BayDirection);
            }
            else {
                this.createCapplates(tierData.Columns, tierData.Beams, currentHeight, drawing.Floor.BayDirection);
            }
            this.createDecking(tierData.Size.XDim, tierData.Size.YDim, currentHeight, tierData.Cutouts);
        }
    }
    createLoadingScreen() {
        const styleSheet = document.createElement('style');
        styleSheet.appendChild(document.createTextNode(loadingScreenCss));
        this.options.containerElement.appendChild(styleSheet);
        const loader = document.createElement('div');
        loader.id = 'loader-3d-viewer';
        this.loadingScreen = document.createElement('div');
        this.loadingScreen.id = 'loading-screen-3d-viewer';
        this.loadingScreen.style.maxWidth = `${this.width}px`;
        this.loadingScreen.style.maxHeight = `${this.height}px`;
        this.loadingScreen.appendChild(loader);
        this.options.containerElement.appendChild(this.loadingScreen);
    }
    onTransitionEnd(event) {
        if (event.target instanceof HTMLElement) {
            event.target.remove();
        }
    }
    toggleDecking(visible) {
        if (!this.canRender) {
            return;
        }
        for (const deckingMesh of this.deckingMeshes) {
            deckingMesh.visible = visible ? visible : !deckingMesh.visible;
        }
    }
    createDecking(xDim, yDim, tierHeight, cutouts) {
        const geometry = new BoxGeometry(xDim * 10, 0.2, yDim * 10);
        const loader = new TextureLoader(this.manager);
        loader.load(this.options.baseUrl + 'particle-board.jpg', texture => {
            texture.wrapS = RepeatWrapping;
            texture.wrapT = RepeatWrapping;
            texture.repeat.set(8, 8);
            texture.encoding = GammaEncoding;
            const material = new MeshBasicMaterial({
                map: texture
            });
            const deckMesh = new Mesh(geometry, material);
            const bspDeck = CSG.fromMesh(deckMesh);
            deckMesh.updateMatrix();
            let bspResult = bspDeck.clone();
            for (const cutout of cutouts) {
                const cutoutGeom = new BoxGeometry(cutout.Size.XDim * 10, 0.2, cutout.Size.YDim * 10);
                const cutoutMesh = new Mesh(cutoutGeom);
                cutoutMesh.position.x = cutout.Position.X + cutout.Size.XDim / 2 - xDim / 2;
                cutoutMesh.position.z = cutout.Position.Y + cutout.Size.YDim / 2 - yDim / 2;
                cutoutMesh.position.multiplyScalar(10);
                cutoutMesh.updateMatrix();
                const bspCut = CSG.fromMesh(cutoutMesh);
                bspResult = bspResult.subtract(bspCut);
            }
            const deckingMesh = CSG.toMesh(bspResult, deckMesh.matrix);
            deckingMesh.material = deckMesh.material;
            deckingMesh.position.x = xDim / 2;
            deckingMesh.position.z = yDim / 2;
            deckingMesh.position.y = tierHeight + this.deckingOffset;
            deckingMesh.position.multiplyScalar(10);
            this.deckingMeshes.push(deckingMesh);
            this.scene.add(deckingMesh);
        }, undefined, this.options.onError);
    }
    createBeams(beams, tierHeight, bayDirection) {
        const loader = new GLTFLoader(this.manager);
        loader.load(this.options.baseUrl + encodeURI('Beam UB254x102x22/Beam UB254x102x22.gltf'), gltf => {
            const mesh = gltf.scene.children[0];
            mesh.rotation.y = bayDirection === 1 ? (270 * Math.PI) / 180 : Math.PI;
            for (const beamData of beams) {
                const beamMesh = mesh.clone();
                beamMesh.scale.x = this.scaleFactor;
                beamMesh.scale.y = this.scaleFactor;
                beamMesh.scale.z = bayDirection === 1 ?
                    this.scaleFactor * (beamData.End.X - beamData.Start.X) :
                    this.scaleFactor * (beamData.End.Y - beamData.Start.Y);
                beamMesh.position.x = beamData.Start.X;
                beamMesh.position.z = beamData.Start.Y;
                beamMesh.position.y = beamData.ConstructionType === 'Standard' ? tierHeight - 0.145 : tierHeight;
                beamMesh.position.multiplyScalar(10);
                this.beamMeshes.push(beamMesh);
                this.scene.add(beamMesh);
            }
        }, undefined, this.options.onError);
    }
    createJoists(joists, columns, tierHeight, bayDirection) {
        const loader = new GLTFLoader(this.manager);
        loader.load(this.options.baseUrl + encodeURI('Joist Ayrshire A140/Joist Ayrshire A140.gltf'), gltf => {
            const mesh = gltf.scene.children[0];
            mesh.rotation.y = bayDirection === 1 ? Math.PI : (270 * Math.PI) / 180;
            for (const joistData of joists) {
                const joistMesh = mesh.clone();
                joistMesh.scale.x = this.scaleFactor;
                joistMesh.scale.y = this.scaleFactor;
                joistMesh.scale.z = bayDirection === 1 ?
                    this.scaleFactor * (joistData.End.Y - joistData.Start.Y) :
                    this.scaleFactor * (joistData.End.X - joistData.Start.X);
                joistMesh.position.x = joistData.Start.X;
                joistMesh.position.z = joistData.Start.Y;
                joistMesh.position.y = tierHeight + 0.11;
                if (joistData.Connections.Columns.length > 0) {
                    let firstColumn;
                    let secondColumn;
                    const cleatOffset = 0.113;
                    for (const connection of joistData.Connections.Columns) {
                        const column = columns.find(c => c.Id === connection.Id);
                        if ((bayDirection === 1 && column.Position.Y < joistData.Start.Y) ||
                            (bayDirection === 2 && column.Position.X < joistData.Start.X)) {
                            firstColumn = column;
                        }
                        else {
                            secondColumn = column;
                        }
                    }
                    if (firstColumn) {
                        if (bayDirection === 1) {
                            joistMesh.position.z = firstColumn.Position.Y + cleatOffset;
                        }
                        else {
                            joistMesh.position.x = firstColumn.Position.X + cleatOffset;
                        }
                    }
                    if (secondColumn) {
                        joistMesh.scale.z = bayDirection === 1 ?
                            this.scaleFactor * (secondColumn.Position.Y - joistMesh.position.z - cleatOffset) :
                            this.scaleFactor * (secondColumn.Position.X - joistMesh.position.x - cleatOffset);
                    }
                }
                joistMesh.position.multiplyScalar(10);
                this.joistMeshes.push(joistMesh);
                this.scene.add(joistMesh);
            }
        }, undefined, this.options.onError);
    }
    createCleats(joists, beams, columns, tierHeight, bayDirection) {
        const loader = new GLTFLoader(this.manager);
        loader.load(this.options.baseUrl + encodeURI('Cleat Ayrshire C140/Cleat Ayrshire C140.gltf'), gltf => {
            const mesh = gltf.scene.children[0];
            mesh.scale.set(this.scaleFactor, this.scaleFactor, this.scaleFactor);
            const heightOffset = 0.18;
            const beamOffset = 0.085;
            const columnOffset = 0.035;
            for (const joistData of joists) {
                const cleat1Mesh = mesh.clone();
                const cleat2Mesh = mesh.clone();
                let leftCleat = false;
                let rightCleat = false;
                let leftColumn;
                let rightColumn;
                for (const connection of joistData.Connections.Beams) {
                    const beam = beams.find(b => b.Id === connection.Id);
                    if (!beam)
                        continue;
                    if (bayDirection === 1) {
                        if (beam.Start.Y < joistData.Start.Y) {
                            leftCleat = true;
                        }
                        else if (beam.End.Y > joistData.End.Y) {
                            rightCleat = true;
                        }
                    }
                    else {
                        if (beam.Start.X < joistData.Start.X) {
                            leftCleat = true;
                        }
                        else if (beam.End.X > joistData.End.X) {
                            rightCleat = true;
                        }
                    }
                }
                for (const connection of joistData.Connections.Columns) {
                    const column = columns.find(c => c.Id === connection.Id);
                    if (round(column.Position.X, 2) < round(joistData.Start.X, 2) || round(column.Position.Y, 2) < round(joistData.Start.Y, 2)) {
                        leftCleat = true;
                        leftColumn = column;
                    }
                    else if (round(column.Position.X, 2) > round(joistData.Start.X, 2) || round(column.Position.Y, 2) > round(joistData.Start.Y, 2)) {
                        rightCleat = true;
                        rightColumn = column;
                    }
                }
                if (bayDirection === 1) {
                    if (leftCleat) {
                        cleat1Mesh.rotation.y = Math.PI;
                        cleat1Mesh.position.x = joistData.Start.X;
                        cleat1Mesh.position.z = joistData.Start.Y - beamOffset;
                    }
                    if (leftColumn) {
                        cleat1Mesh.position.x = leftColumn.Position.X;
                        cleat1Mesh.position.z = leftColumn.Position.Y + columnOffset;
                    }
                    if (rightCleat) {
                        cleat2Mesh.rotation.z = Math.PI;
                        cleat2Mesh.position.x = joistData.End.X;
                        cleat2Mesh.position.z = joistData.End.Y + beamOffset;
                    }
                    if (rightColumn) {
                        cleat2Mesh.position.x = rightColumn.Position.X;
                        cleat2Mesh.position.z = rightColumn.Position.Y - columnOffset;
                    }
                }
                else {
                    if (leftCleat) {
                        cleat1Mesh.rotation.y = (270 * Math.PI) / 180;
                        cleat1Mesh.position.x = joistData.Start.X - beamOffset;
                        cleat1Mesh.position.z = joistData.Start.Y;
                    }
                    if (leftColumn) {
                        cleat1Mesh.position.x = leftColumn.Position.X + columnOffset;
                        cleat1Mesh.position.z = leftColumn.Position.Y;
                    }
                    if (rightCleat) {
                        cleat2Mesh.rotation.z = Math.PI;
                        cleat2Mesh.rotation.y = (90 * Math.PI) / 180;
                        cleat2Mesh.position.x = joistData.End.X + beamOffset;
                        cleat2Mesh.position.z = joistData.End.Y;
                    }
                    if (rightColumn) {
                        cleat2Mesh.position.x = rightColumn.Position.X - columnOffset;
                        cleat2Mesh.position.z = rightColumn.Position.Y;
                    }
                }
                if (leftCleat) {
                    cleat1Mesh.position.y = tierHeight + heightOffset;
                    cleat1Mesh.position.multiplyScalar(10);
                    this.cleatMeshes.push(cleat1Mesh);
                    this.scene.add(cleat1Mesh);
                }
                if (rightCleat) {
                    cleat2Mesh.position.y = tierHeight + heightOffset;
                    cleat2Mesh.position.multiplyScalar(10);
                    this.cleatMeshes.push(cleat2Mesh);
                    this.scene.add(cleat2Mesh);
                }
            }
        }, undefined, this.options.onError);
    }
    createColumns(columns, beams, baseHeight, tierHeight, multiTier, topTier) {
        const loader = new GLTFLoader(this.manager);
        const capplateOffset = 0.011;
        loader.load(this.options.baseUrl + encodeURI('Column SHS70x70x3.5/Column SHS70x70x3.5.gltf'), gltf => {
            const mesh = gltf.scene.children[0];
            mesh.scale.set(this.scaleFactor, multiTier ? this.scaleFactor * (tierHeight + this.deckingOffset) : this.scaleFactor * (tierHeight - capplateOffset), this.scaleFactor);
            for (const columnData of columns) {
                const columnMesh = mesh.clone();
                if (baseHeight !== 0) {
                    columnMesh.scale.y = this.scaleFactor * tierHeight;
                    columnMesh.scale.y -= topTier ? this.scaleFactor * 0.02 : 0;
                    if (columnData.Connections.Beams.length > 0) {
                        if (columnData.Connections.Capplates.length > 0) {
                            columnMesh.scale.y = this.scaleFactor * (tierHeight - this.deckingOffset);
                        }
                    }
                }
                else if (columnData.Connections.Beams.length > 0) {
                    const beam = beams.find(b => b.Id === columnData.Connections.Beams[0].Id);
                    if (beam.ConstructionType === 'Standard') {
                        const offset = multiTier ? tierHeight + this.deckingOffset :
                            tierHeight - this.standardBeamOffset;
                        columnMesh.scale.y = this.scaleFactor * offset;
                    }
                }
                else if ((columnData.IsFlagpost || columnData.IsGoalpost) && multiTier) {
                    columnMesh.scale.y = this.scaleFactor * (tierHeight + this.deckingOffset);
                }
                else if ((columnData.IsFlagpost || columnData.IsGoalpost) && !multiTier) {
                    const angleOffset = 0.11;
                    columnMesh.scale.y = this.scaleFactor * (tierHeight + angleOffset);
                }
                columnMesh.position.x = columnData.Position.X;
                columnMesh.position.z = columnData.Position.Y;
                columnMesh.position.y = baseHeight === 0 ? baseHeight : baseHeight + this.deckingOffset;
                columnMesh.position.multiplyScalar(10);
                this.columnMeshes.push(columnMesh);
                this.scene.add(columnMesh);
            }
        }, undefined, this.options.onError);
    }
    createBaseplates(columns) {
        const loader = new GLTFLoader(this.manager);
        loader.load(this.options.baseUrl + encodeURI('Baseplate 300x300x20/Baseplate 300x300x20.gltf'), gltf => {
            const mesh = gltf.scene.children[0];
            mesh.scale.set(this.scaleFactor, this.scaleFactor, this.scaleFactor);
            for (const columnData of columns) {
                const bpMesh = mesh.clone();
                bpMesh.position.x = columnData.Position.X;
                bpMesh.position.z = columnData.Position.Y;
                bpMesh.position.multiplyScalar(10);
                this.bpMeshes.push(bpMesh);
                this.scene.add(bpMesh);
            }
        }, undefined, this.options.onError);
    }
    createCapplates(columns, beams, tierHeight, bayDirection) {
        const loader = new GLTFLoader(this.manager);
        loader.load(this.options.baseUrl + encodeURI('Capplate 200x100x10/Capplate 200x100x10.gltf'), gltf => {
            const mesh = gltf.scene.children[0];
            mesh.scale.set(this.scaleFactor, this.scaleFactor, this.scaleFactor);
            mesh.rotation.y = bayDirection === 1 ? (90 * Math.PI) / 180 : Math.PI;
            for (const columnData of columns) {
                if (!columnData.IsFlagpost && !columnData.IsGoalpost && columnData.Connections.Capplates.length > 0) {
                    const cpMesh = mesh.clone();
                    const beam = beams.find(b => b.Id === columnData.Connections.Beams[0].Id);
                    const heightOffset = beam.ConstructionType === 'Inset' ? 0.01 : 0.155;
                    cpMesh.position.x = columnData.Position.X;
                    cpMesh.position.z = columnData.Position.Y;
                    cpMesh.position.y = tierHeight - heightOffset;
                    cpMesh.position.multiplyScalar(10);
                    this.cpMeshes.push(cpMesh);
                    this.scene.add(cpMesh);
                }
            }
        }, undefined, this.options.onError);
    }
    createAngles(angles, tierHeight) {
        const loader = new GLTFLoader(this.manager);
        const heightOffset = 0.035;
        const zOffset = 0.011;
        const xOffset = 0.01;
        loader.load(this.options.baseUrl + encodeURI('Angle 50x75x6.0/Angle 50x75x6.0.gltf'), gltf => {
            const mesh = gltf.scene.children[0];
            for (const angleData of angles) {
                const angleMesh = mesh.clone();
                angleMesh.scale.x = this.scaleFactor;
                angleMesh.scale.y = this.scaleFactor;
                angleMesh.position.y = tierHeight + heightOffset;
                switch (angleData.CutoutDirection) {
                    case CutoutDirection.Above:
                        angleMesh.rotateY(270 * Math.PI / 180);
                        angleMesh.scale.z = this.scaleFactor * (angleData.End.X - angleData.Start.X);
                        angleMesh.position.x = angleData.Position.X;
                        angleMesh.position.z = angleData.Position.Y + zOffset;
                        break;
                    case CutoutDirection.Below:
                        angleMesh.rotateY(270 * Math.PI / 180);
                        angleMesh.applyMatrix4(new Matrix4().makeScale(1, 1, -1));
                        angleMesh.scale.z = this.scaleFactor * (angleData.End.X - angleData.Start.X);
                        angleMesh.position.x = angleData.Position.X;
                        angleMesh.position.z = angleData.Position.Y - zOffset;
                        break;
                    case CutoutDirection.Left:
                        angleMesh.rotateY(Math.PI);
                        angleMesh.applyMatrix4(new Matrix4().makeScale(-1, 1, 1));
                        angleMesh.scale.z = this.scaleFactor * (angleData.End.Y - angleData.Start.Y);
                        angleMesh.position.x = angleData.Position.X + xOffset;
                        angleMesh.position.z = angleData.Position.Y;
                        break;
                    case CutoutDirection.Right:
                        angleMesh.rotateY(Math.PI);
                        angleMesh.scale.z = this.scaleFactor * (angleData.End.Y - angleData.Start.Y);
                        angleMesh.position.x = angleData.Position.X - xOffset;
                        angleMesh.position.z = angleData.Position.Y;
                        break;
                }
                angleMesh.position.multiplyScalar(10);
                this.angleMeshes.push(angleMesh);
                this.scene.add(angleMesh);
            }
        }, undefined, this.options.onError);
    }
    createBeamTags(beams, tierHeight, bayDirection) {
        const loader = new GLTFLoader(this.manager);
        const heightOffset = 0.04;
        const lengthOffset = 0.03;
        const widthOffset = 0.01;
        loader.load(this.options.baseUrl + encodeURI('Beam Tag 170x75x15/Beam Tag 170x75x15.gltf'), gltf => {
            const mesh = gltf.scene.children[0];
            mesh.scale.set(this.scaleFactor, this.scaleFactor, this.scaleFactor);
            for (const beamData of beams) {
                const leftTagMesh = mesh.clone();
                const rightTagMesh = mesh.clone();
                if (bayDirection === 1) {
                    leftTagMesh.position.x = beamData.Start.X - lengthOffset;
                    leftTagMesh.position.z = beamData.Start.Y - widthOffset;
                    rightTagMesh.rotation.y = 180 * Math.PI / 180;
                    rightTagMesh.position.x = beamData.End.X + lengthOffset;
                    rightTagMesh.position.z = beamData.End.Y - widthOffset;
                }
                else {
                    leftTagMesh.rotation.y = 270 * Math.PI / 180;
                    leftTagMesh.position.x = beamData.Start.X - widthOffset;
                    leftTagMesh.position.z = beamData.Start.Y - lengthOffset;
                    rightTagMesh.rotation.y = 90 * Math.PI / 180;
                    rightTagMesh.position.x = beamData.End.X - widthOffset;
                    rightTagMesh.position.z = beamData.End.Y + lengthOffset;
                }
                if (beamData.ConstructionType === 'Standard') {
                    leftTagMesh.position.y = tierHeight + heightOffset - this.standardBeamOffset;
                    rightTagMesh.position.y = tierHeight + heightOffset - this.standardBeamOffset;
                }
                else {
                    leftTagMesh.position.y = tierHeight + heightOffset;
                    rightTagMesh.position.y = tierHeight + heightOffset;
                }
                leftTagMesh.position.multiplyScalar(10);
                rightTagMesh.position.multiplyScalar(10);
                this.tagMeshes.push(leftTagMesh, rightTagMesh);
                this.scene.add(leftTagMesh);
                this.scene.add(rightTagMesh);
            }
        }, undefined, this.options.onError);
    }
}
